<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import AddTranslation from './AddTranslation.vue';
import ImportCanned from './ImportCanned.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showImportPopup = ref(false);
const isExporting = ref(false);
const isDeletingAll = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const showDeleteAllConfirmationPopup = ref(false);
const showTranslationsPopup = ref(false);
const showAddTranslationPopup = ref(false);
const selectedLocale = ref('');
const selectedLocaleName = ref('');
const selectedTranslation = ref(null);
const activeResponse = ref({});
const existingTranslations = ref([]);
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openImportPopup = () => {
  showImportPopup.value = true;
};
const hideImportPopup = () => {
  showImportPopup.value = false;
};

const triggerExport = async () => {
  if (isExporting.value) {
    return;
  }
  isExporting.value = true;
  try {
    await store.dispatch('exportCannedResponses');
    useAlert(t('CANNED_MGMT.EXPORT.SUCCESS'));
  } catch (error) {
    const message = error?.message || t('CANNED_MGMT.EXPORT.ERROR');
    useAlert(message);
  } finally {
    isExporting.value = false;
  }
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openTranslationsPopup = async (response) => {
  activeResponse.value = response;
  // Fetch existing translations
  try {
    const translationsResponse = await store.dispatch('getCannedResponseTranslations', response.id);
    existingTranslations.value = translationsResponse || [];
  } catch (error) {
    existingTranslations.value = [];
  }
  showTranslationsPopup.value = true;
};
const hideTranslationsPopup = () => {
  showTranslationsPopup.value = false;
  existingTranslations.value = [];
};

const hasTranslation = (locale) => {
  return existingTranslations.value.some(t => t.locale === locale);
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage = error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = async () => {
  closeDeletePopup();
  loading.value[activeResponse.value.id] = true;
  await deleteCannedResponse(activeResponse.value.id);
};

const openDeleteAllPopup = () => {
  showDeleteAllConfirmationPopup.value = true;
};

const closeDeleteAllPopup = () => {
  showDeleteAllConfirmationPopup.value = false;
};

const confirmDeleteAll = async () => {
  closeDeleteAllPopup();
  isDeletingAll.value = true;
  try {
    await store.dispatch('deleteAllCannedResponses');
    useAlert(t('CANNED_MGMT.DELETE_ALL.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage = error?.message || t('CANNED_MGMT.DELETE_ALL.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isDeletingAll.value = false;
  }
};

const supportedLanguages = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'it', name: 'Italian' },
  { code: 'es', name: 'Spanish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'fi', name: 'Finnish' },
  { code: 'da', name: 'Danish' },
  { code: 'sv', name: 'Swedish' },
  { code: 'no', name: 'Norwegian (Bokmål)' },
];

const getLanguageName = (code) => {
  const lang = supportedLanguages.find(l => l.code === code);
  return lang ? lang.name : code;
};

const addTranslation = (locale) => {
  selectedLocale.value = locale;
  selectedLocaleName.value = getLanguageName(locale);
  // Find existing translation for this locale
  selectedTranslation.value = existingTranslations.value.find(t => t.locale === locale) || null;
  showTranslationsPopup.value = false;
  showAddTranslationPopup.value = true;
};

const hideAddTranslationPopup = async () => {
  showAddTranslationPopup.value = false;
  selectedTranslation.value = null;
  
  // Refresh translations to update the list
  try {
    const translationsResponse = await store.dispatch('getCannedResponseTranslations', activeResponse.value.id);
    existingTranslations.value = translationsResponse || [];
  } catch (error) {
    existingTranslations.value = [];
  }
  
  showTranslationsPopup.value = true;
};

const tableHeaders = [
  t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
];
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('CANNED_MGMT.HEADER')"
      :description="$t('CANNED_MGMT.DESCRIPTION')"
      :link-text="$t('CANNED_MGMT.LEARN_MORE')"
      feature-name="canned_responses"
    >
      <template #actions>
        <Button
          v-if="records.length > 0"
          icon="i-lucide-trash-2"
          label="Delete All"
          ruby
          faded
          :is-loading="isDeletingAll"
          @click="openDeleteAllPopup"
        />
        <Button
          icon="i-lucide-upload"
          :label="$t('CANNED_MGMT.IMPORT.BUTTON_TEXT')"
          slate
          faded
          @click="openImportPopup"
        />
        <Button
          icon="i-lucide-download"
          :label="$t('CANNED_MGMT.EXPORT.BUTTON_TEXT')"
          slate
          faded
          :is-loading="isExporting"
          @click="triggerExport"
        />
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
          @click="openAddPopup"
        />
      </template>
    </BaseSettingsHeader>

    <div class="mt-6 flex-1">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('CANNED_MGMT.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base text-n-slate-11 py-8"
      >
        {{ $t('CANNED_MGMT.LIST.404') }}
      </p>
      <table v-else class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right"
          >
            <span v-if="thHeader !== tableHeaders[0]">
              {{ thHeader }}
            </span>
            <button
              v-else
              class="flex items-center p-0 cursor-pointer"
              @click="toggleSort"
            >
              <span class="mb-0">
                {{ thHeader }}
              </span>
              <fluent-icon
                class="ml-2 size-4"
                :icon="sortOrder === 'desc' ? 'chevron-up' : 'chevron-down'"
              />
            </button>
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="(cannedItem, index) in records"
            :key="cannedItem.short_code"
          >
            <td
              class="py-4 ltr:pr-4 rtl:pl-4 truncate max-w-xs font-medium"
              :title="cannedItem.short_code"
            >
              {{ cannedItem.short_code }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 md:break-all whitespace-normal">
              {{ cannedItem.content }}
            </td>
            <td class="py-4 flex justify-end gap-1">
              <Button
                v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click="openEditPopup(cannedItem)"
              />
              <Button
                v-tooltip.top="'Manage Translations'"
                icon="i-lucide-languages"
                slate
                xs
                faded
                @click="openTranslationsPopup(cannedItem)"
              />
              <Button
                v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[cannedItem.id]"
                @click="openDeletePopup(cannedItem, index)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showImportPopup" :on-close="hideImportPopup">
      <ImportCanned v-if="showImportPopup" :on-close="hideImportPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :edmetadata="activeResponse.metadata"
        :edai-instructions="activeResponse.ai_instructions"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showTranslationsPopup" :on-close="hideTranslationsPopup">
      <div v-if="showTranslationsPopup" class="flex flex-col h-auto">
        <woot-modal-header
          :header-title="`Manage Translations - ${activeResponse.short_code}`"
          header-content="Add and manage translations for this template in different languages"
        />
        <div class="p-6">
          <p class="text-sm text-slate-600 mb-4">
            Supported Languages: English, French, Italian, Spanish, Dutch, Finnish, Danish, Swedish, Norwegian (Bokmål)
          </p>
          <div class="space-y-3 max-h-96 overflow-y-auto pr-2">
            <div v-for="lang in supportedLanguages" :key="lang.code" 
                 class="flex items-center justify-between p-3 border rounded">
              <div>
                <span class="font-medium">{{ lang.name }}</span>
                <span v-if="lang.code === 'en'" class="ml-2 text-xs text-slate-500">(Default)</span>
              </div>
              <Button
                v-if="lang.code === 'en'"
                disabled
                slate
                xs
                label="Default Language"
              />
              <Button
                v-else
                slate
                xs
                :label="hasTranslation(lang.code) ? 'Edit Translation' : 'Add Translation'"
                @click="addTranslation(lang.code)"
              />
            </div>
          </div>
          <div class="flex justify-end mt-6">
            <Button
              slate
              label="Close"
              @click="hideTranslationsPopup"
            />
          </div>
        </div>
      </div>
    </woot-modal>

    <AddTranslation
      v-if="showAddTranslationPopup"
      :canned-response-id="activeResponse.id"
      :locale="selectedLocale"
      :locale-name="selectedLocaleName"
      :existing-content="selectedTranslation?.content || ''"
      :translation-id="selectedTranslation?.id || null"
      :on-close="hideAddTranslationPopup"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-delete-modal
      v-model:show="showDeleteAllConfirmationPopup"
      :on-close="closeDeleteAllPopup"
      :on-confirm="confirmDeleteAll"
      title="Delete All Templates?"
      message="Are you sure you want to delete ALL canned response templates? This action cannot be undone."
      :message-value="` ${records.length} templates will be permanently deleted.`"
      confirm-text="Yes, Delete All"
      reject-text="Cancel"
    />
  </div>
</template>
